import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, HardDrive, Play, Loader2, Layers } from 'lucide-react';
import { customerApi, Collectiv3Scene, ProgressionScene } from '../services/customerApi';
import { useAuth } from '../contexts/AuthContext';

const logo = '/logo.gif';

type ShowcaseTile =
  | { kind: 'collectiv3'; scene: Collectiv3Scene }
  | { kind: 'progression'; scene: ProgressionScene };

const ShowcasePage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  const [tiles, setTiles] = useState<ShowcaseTile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScenes = async () => {
    try {
      setLoading(true);
      setError(null);

      const [collectiv3Res, progressionsRes] = await Promise.allSettled([
        customerApi.getAllCollectiv3Scenes({ category: 'Showcase' }),
        customerApi.getAllProgressions(),
      ]);

      const collectiv3Tiles: ShowcaseTile[] =
        collectiv3Res.status === 'fulfilled'
          ? collectiv3Res.value.data
              .filter(s => s.category?.toLowerCase() === 'showcase')
              .map(s => ({ kind: 'collectiv3', scene: s }))
          : [];

      const progressionTiles: ShowcaseTile[] =
        progressionsRes.status === 'fulfilled'
          ? progressionsRes.value.data
              .filter(s => s.category?.toLowerCase() === 'showcase')
              .map(s => ({ kind: 'progression', scene: s }))
          : [];

      setTiles([...collectiv3Tiles, ...progressionTiles]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load showcase scenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScenes(); }, []);

  const handleTileClick = (tile: ShowcaseTile) => {
    if (tile.kind === 'collectiv3') {
      navigate(`/viewer?scene=${encodeURIComponent(tile.scene.sceneId)}`);
    } else {
      navigate(`/multi-viewer?scene=${encodeURIComponent(tile.scene.sceneId)}`);
    }
  };

  const getTileName = (tile: ShowcaseTile) =>
    tile.kind === 'collectiv3' ? tile.scene.sceneName : tile.scene.sceneName;

  const getTileDescription = (tile: ShowcaseTile) =>
    tile.kind === 'collectiv3' ? tile.scene.cardDescription : tile.scene.description;

  const getTileCredits = (tile: ShowcaseTile) =>
    tile.kind === 'collectiv3' ? tile.scene.credits : undefined;

  const getTileLocation = (tile: ShowcaseTile) =>
    tile.kind === 'collectiv3' ? tile.scene.location : undefined;

  const getTileThumbnail = (tile: ShowcaseTile) =>
    tile.kind === 'collectiv3' ? tile.scene.thumbnail : undefined;

  return (
    <div className="min-h-screen bg-[#161513]">
      {/* Public header for unauthenticated visitors */}
      {!authenticated && (
        <header className="sticky top-0 z-50 w-full bg-[#0E0D0C]/95 backdrop-blur-lg border-b border-[#0CB4E4]/20 py-3 px-3 sm:px-6 sm:py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1.5 sm:gap-3">
              <img src={logo} alt="CR3ATE.AI" className="h-6 sm:h-10" />
              <h1 className="font-light text-base sm:text-2xl text-[#0CB4E4] font-brand">CR3ATE.AI</h1>
            </Link>
            <div className="flex items-center gap-1.5 sm:gap-4">
              <Link to="/login" className="px-2.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-base text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/request-waitlist" className="px-3 py-1.5 sm:px-5 sm:py-2.5 text-xs sm:text-base bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] rounded-lg sm:rounded-xl hover:shadow-lg transition-all font-medium">Join the waitlist</Link>
            </div>
          </div>
        </header>
      )}

      {/* Page header */}
      <div className="relative bg-[#161513] border-b border-[#0CB4E4]/20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
          {authenticated && (
            <div className="flex items-center mb-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
            </div>
          )}
          <div className="text-center">
            <h1 className="text-4xl sm:text-6xl font-bold text-white mb-4">
              <span className="text-[#0CB4E4]">Showcase</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore our curated collection of immersive 3D scenes captured with CR3ATE.AI technology
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {loading && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#0CB4E4] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Loading Showcase…</h3>
            <p className="text-gray-300">Preparing our 3D collection for you</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 text-red-400 p-6 rounded-xl border border-red-500/30 mb-8">
            <h3 className="font-medium mb-2">Unable to Load Showcase</h3>
            <p>{error}</p>
            <button
              onClick={fetchScenes}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && tiles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tiles.map((tile) => {
              const thumbnail = getTileThumbnail(tile);
              const name = getTileName(tile);
              const description = getTileDescription(tile);
              const credits = getTileCredits(tile);
              const location = getTileLocation(tile);
              const key = `${tile.kind}-${tile.scene.sceneId}`;

              return (
                <div
                  key={key}
                  onClick={() => handleTileClick(tile)}
                  className="relative overflow-hidden h-[480px] bg-[#161513] shadow-lg border border-[#0CB4E4]/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] rounded-xl group cursor-pointer"
                >
                  {/* Top half — thumbnail */}
                  <div className="absolute inset-0 w-full h-full bg-[#161513] overflow-hidden rounded-xl">
                    <div className="h-1/2 bg-gradient-to-br from-[#0CB4E4]/20 via-[#0CB4E4]/10 to-[#0CB4E4]/20 relative flex items-center justify-center">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={name}
                          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = logo;
                            (e.target as HTMLImageElement).className = 'h-24 object-contain opacity-60';
                          }}
                        />
                      ) : (
                        <img src={logo} alt="3D Scene" className="h-24 object-contain opacity-60 transition-transform duration-500 ease-out group-hover:scale-110" />
                      )}

                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="w-16 h-16 bg-[#0CB4E4] rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-[#161513] ml-1" />
                        </div>
                      </div>

                      {/* Source type badge */}
                      {tile.kind === 'progression' && (
                        <div className="absolute top-3 left-3 backdrop-blur-sm px-2.5 py-1 text-xs font-medium border rounded-full flex items-center gap-1 bg-purple-500/20 text-purple-300 border-purple-500/30">
                          <Layers className="w-3 h-3" />
                          <span>Timeline</span>
                        </div>
                      )}
                    </div>

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#161513] via-transparent to-transparent" />
                  </div>

                  {/* Bottom info panel */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 z-10 bg-gradient-to-t from-[#161513] via-[#161513]/95 to-transparent min-h-[240px] flex flex-col justify-end">
                    <h3 className="font-semibold text-white text-base mb-2 line-clamp-1" title={name}>
                      {name}
                    </h3>

                    {description && (
                      <p className="text-sm text-gray-300 line-clamp-3 mb-3" title={description}>
                        {description}
                      </p>
                    )}

                    {credits && (
                      <div className="mb-3">
                        <span className="text-xs text-gray-400">Credits: </span>
                        <span className="text-xs text-[#0CB4E4] font-medium">{credits}</span>
                      </div>
                    )}

                    {location && (
                      <div className="mb-3">
                        <span className="text-xs text-gray-400">Location: </span>
                        <span className="text-xs text-gray-300 font-medium">{location}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        <span>3D Scene</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Play className="w-3 h-3" />
                        <span>Interactive</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && tiles.length === 0 && (
          <div className="text-center py-20">
            <img src={logo} alt="No scenes" className="w-16 h-16 mx-auto mb-4 object-contain opacity-50" />
            <h3 className="text-lg font-medium text-white mb-2">No Showcase Scenes Yet</h3>
            <p className="text-gray-300">Check back soon — scenes tagged "Showcase" will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowcasePage;
